import { DebugLogger } from "./debugLogger.js";

const PERSIST_STATE_KEY = "artemis_persist_state";
const CHECK_CACHE_KEY = "__artemisPersistenceCheck";

// Requests durable storage for the origin and reports the outcome. Without
// the grant, Android Chrome treats IndexedDB as best-effort and may evict ALL
// offline route data under storage pressure. The grant is decided
// automatically (installed PWA / site engagement) — there is no user prompt —
// so callers must surface a warning UI when it stays denied.
//
// Memoized per page load (on window, which also resets per test environment):
// capture pages reload on every sample and several modules check on connect,
// so all callers share one browser round trip.
export function ensurePersistentStorage() {
  return (window[CHECK_CACHE_KEY] ??= checkPersistentStorage());
}

async function checkPersistentStorage() {
  if (!navigator.storage?.persist || !navigator.storage?.persisted) {
    return { supported: false, persisted: false };
  }

  try {
    let persisted = await navigator.storage.persisted();
    if (!persisted) {
      persisted = await navigator.storage.persist();
    }
    logStateTransition(persisted);
    return { supported: true, persisted };
  } catch (error) {
    DebugLogger.warn("Persistent storage check failed", {
      error: error?.message,
    });
    return { supported: true, persisted: false };
  }
}

// Log only when the state changes — an unconditional warn would rewrite the
// (large) debug-log blob on every one of up to 10k per-sample page loads and
// flood the 500-entry buffer, precisely on storage-stressed devices.
function logStateTransition(persisted) {
  const state = persisted ? "granted" : "denied";
  try {
    if (sessionStorage.getItem(PERSIST_STATE_KEY) === state) return;
    sessionStorage.setItem(PERSIST_STATE_KEY, state);
  } catch {
    // sessionStorage unavailable — fall through and log every time.
  }

  if (persisted) {
    DebugLogger.info("Persistent storage granted");
  } else {
    DebugLogger.warn(
      "Persistent storage denied — offline data may be evicted under storage pressure",
    );
  }
}
